// src/store/useAuthStore.ts
import {create} from "zustand";
import {getAuthToken, logout, login, refreshToken} from "../services/authService";
import {setAuthToken} from "../services/httpService";
import useUserStore from "./useUserStore.ts";
import {fetchUser} from "../services/userService.ts";
import {IUser} from "../models/interfaces/user.interface.ts";
import {sleep} from "../utils/utils.ts";
import useWorkspaceStore from "./useWorkspaceStrore.ts";

interface IAuthState {
  isAuthenticated: boolean;
  error: string | null;
  loading: boolean;
  initializeAuth: () => Promise<void>; // Auto-login function
  login: (username: string, password: string) => Promise<void>;
  refreshToken: () => Promise<void>;
  logout: () => void;
}

const useAuthStore = create<IAuthState>((set) => ({
  isAuthenticated: false,
  error: null,
  loading: true, // Add a loading state to track whether the app is trying to auto-login

  // Initialize auth (check if the user is logged in on app load)
  initializeAuth: async () => {
    const token: string | null = getAuthToken();
    if (token) {
      try {
        setAuthToken(token);
        const user: IUser = await fetchUser();
        set({isAuthenticated: true, error: null});
        useUserStore.getState().initUser(user); // initialize the user store with the user data
        useWorkspaceStore.getState().getWorkspaceMembers().then(() => null);
      } catch (err: unknown) {
        console.error("Auto-login failed:", err);
        set({error: "Something went wrong"});
        logout();
      } finally {
        set({loading: false});
      }
    } else {
      set({loading: false});
    }
  },

  // Login function using email/password
  login: async (username, password) => {
    try {
      set({loading: true});
      // await sleep(100000)
      const {user} = await login(username, password);
      set({isAuthenticated: true, error: null});
      useUserStore.getState().initUser(user); // initialize the user store with the user data
      useWorkspaceStore.getState().getWorkspaceMembers().then(() => null);
    } catch (err: unknown) {
      set({error: "Login failed. Please check your credentials."});
    } finally {
      set({loading: false});
    }
  },

  // Refresh token function
  refreshToken: async () => {
    try {
      set({loading: true});
      const {user} = await refreshToken();
      set({isAuthenticated: true, error: null});
      useUserStore.getState().initUser(user); // initialize the user store with the user data
      useWorkspaceStore.getState().getWorkspaceMembers().then(() => null);
    } catch (err: unknown) {
      console.error("Token refresh failed:", err);
      set({error: "Failed to refresh token"});
    } finally {
      set({loading: false});
    }
  },

  // Logout function
  logout: () => {
    logout();
    set({isAuthenticated: false});
    useUserStore.getState().removeUser(); // Clear user store when logged out
  },
}));

export default useAuthStore;
